import type { AttemptQuestion } from '@/types/api';

/**
 * Brouillon de reponse, tous types de questions confondus.
 * Answer draft, covering every question type.
 */
export interface AnswerDraft {
  selectedChoiceIds: string[];
  /**
   * Remise en ordre : identifiants dans l'ordre propose.
   * Appariement : paires `idDuTerme:idDuTermeAssocie`.
   */
  orderedChoiceIds: string[];
  textAnswer: string;
  /**
   * L'apprenant a-t-il agi sur cette question ? Une remise en ordre part d'un
   * ordre deja constitue : sans ce temoin, elle passerait pour repondue avant
   * meme d'avoir ete lue. | Whether the learner acted on this question.
   */
  touched: boolean;
}

export const emptyDraft = (): AnswerDraft => ({
  selectedChoiceIds: [],
  orderedChoiceIds: [],
  textAnswer: '',
  touched: false,
});

/** Brouillon initial : la remise en ordre part de l'ordre presente. */
export function initialDraft(question: AttemptQuestion): AnswerDraft {
  if (question.type === 'ORDERING') {
    return { ...emptyDraft(), orderedChoiceIds: question.choices.map((choice) => choice.id) };
  }
  return emptyDraft();
}

/**
 * Une question est consideree traitee des qu'elle porte une reponse
 * exploitable. La remise en ordre demande un classement effectif : l'ordre
 * propose au depart ne vaut pas reponse.
 * | Whether the learner has actually answered.
 */
export function isAnswered(question: AttemptQuestion, draft: AnswerDraft | undefined): boolean {
  if (!draft) return false;
  switch (question.type) {
    case 'ORDERING':
      return draft.touched && draft.orderedChoiceIds.length > 0;
    case 'MATCHING':
      return draft.orderedChoiceIds.length === (question.matches?.length ?? 0) && draft.orderedChoiceIds.length > 0;
    case 'FILL_BLANK':
    case 'FREE_TEXT':
      return draft.textAnswer.trim().length > 0;
    default:
      return draft.selectedChoiceIds.length > 0;
  }
}

/** Charge utile envoyee a l'API pour une question. */
export function toPayload(question: AttemptQuestion, draft: AnswerDraft) {
  switch (question.type) {
    case 'ORDERING':
    case 'MATCHING':
      return { questionId: question.id, orderedChoiceIds: draft.orderedChoiceIds };
    case 'FILL_BLANK':
    case 'FREE_TEXT':
      return { questionId: question.id, textAnswer: draft.textAnswer };
    default:
      return { questionId: question.id, selectedChoiceIds: draft.selectedChoiceIds };
  }
}

/** Deplace un element d'une position dans la liste ordonnee. */
export function moveItem(ids: string[], from: number, to: number): string[] {
  if (to < 0 || to >= ids.length) return ids;
  const next = [...ids];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved as string);
  return next;
}

/** Associe un terme de gauche a une cible (ou retire l'association). */
export function setPair(pairs: string[], leftId: string, rightId: string): string[] {
  const kept = pairs.filter((pair) => !pair.startsWith(`${leftId}:`));
  return rightId ? [...kept, `${leftId}:${rightId}`] : kept;
}

/** Cible actuellement associee a un terme de gauche. */
export function pairedWith(pairs: string[], leftId: string): string {
  const found = pairs.find((pair) => pair.startsWith(`${leftId}:`));
  return found ? (found.split(':')[1] ?? '') : '';
}

/**
 * Derniere question atteignable : on ne depasse pas la premiere question
 * laissee sans reponse. | Furthest question the learner may reach.
 */
export function furthestReachable(
  questions: AttemptQuestion[],
  answers: Record<string, AnswerDraft | undefined>,
): number {
  const pending = questions.findIndex((question) => !isAnswered(question, answers[question.id]));
  return pending === -1 ? Math.max(questions.length - 1, 0) : pending;
}
